import {
  createStyles,
  makeStyles,
  Slider,
  Theme,
  Tooltip,
  Typography,
  ValueLabelProps,
  withStyles,
} from '@material-ui/core'
import { AttachMoneyOutlined, WarningOutlined } from '@material-ui/icons'
import classNames from 'classnames'
import { MoneyString } from 'model/Money'
import {
  createContext,
  forwardRef,
  LegacyRef,
  SVGProps,
  useContext,
} from 'react'
import { useTranslation } from 'react-i18next'
import { MoneyDto } from 'services/APIs/InternalAPI/internal-api.contracts'
import { ReactComponent as TargetIcon } from '_icons/target-icon.svg'

const StyledTargetIcon = (props: SVGProps<SVGSVGElement>) => (
  <TargetIcon
    {...props}
    fill="currentColor"
    style={{ width: '42px', height: '42px' }}
    stroke="currentColor"
    strokeWidth={'56px'}
  />
)

type Props = {
  targetPrice: MoneyDto
  salesPrice: MoneyDto
  openTargetPricePopOver?: () => void
  rootClassName?: string
}

function ValueLabelComponent(props: ValueLabelProps & Props) {
  const isSalesPrice = props.value === props.salesPrice.value
  const { t } = useTranslation()

  return (
    <Tooltip
      open={props.open}
      placement={props['index'] === 0 ? 'left' : 'right'}
      enterTouchDelay={0}
      arrow
      title={
        <Typography
          variant="caption"
          color="inherit"
          style={{ whiteSpace: 'pre-line', textAlign: 'center' }}
        >
          {isSalesPrice
            ? `${t('project:price-per-batch')}\n${MoneyString(
                props.salesPrice
              )}`
            : `${t('common:target-price', 'target price')}\n${MoneyString(
                props.targetPrice
              )}`}
        </Typography>
      }
    >
      {props.children}
    </Tooltip>
  )
}

const ThumbRender = (
  props: { ['data-index']: number },
  ref: LegacyRef<HTMLSpanElement>
): JSX.Element => {
  let IconComponent = <StyledTargetIcon />
  const _props = useContext(TargetPriceSliderContext)

  if (_props) {
    if (_props.targetPrice.value < _props.salesPrice.value) {
      if (props['data-index'] === 0) {
        // target
        IconComponent = <StyledTargetIcon />
      } else {
        IconComponent =
          _props.salesPrice.value === 0 ? (
            <WarningOutlined />
          ) : (
            <AttachMoneyOutlined />
          )
      }
    } else {
      if (props['data-index'] === 1) {
        // target
        IconComponent = <StyledTargetIcon />
      } else {
        IconComponent =
          _props.salesPrice.value === 0 ? (
            <WarningOutlined />
          ) : (
            <AttachMoneyOutlined />
          )
      }
    }
  }

  return (
    <span {...props} ref={ref}>
      {IconComponent}
    </span>
  )
}

const ThumbComponent = forwardRef(ThumbRender)

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    success: {
      color: theme.palette.success.main,
    },
    info: {
      color: theme.palette.info.main,
    },
    error: {
      color: theme.palette.error.main,
    },
    warning: {
      color: theme.palette.warning.main,
    },
  })
)

const StyledSlider = withStyles((theme: Theme) =>
  createStyles({
    thumb: {
      // height: 27,
      width: 20,
      backgroundColor: theme.palette.background.paper,
      '& .bar': {
        height: 9,
        width: 2,
        backgroundColor: 'currentColor',
      },
    },
    active: {},
    track: {
      height: 2,
    },
    rail: {
      color: theme.palette.divider,
      opacity: 1,
      height: 3,
    },
  })
)(Slider)

const TargetPriceSliderContext = createContext<Props>(undefined)

export const TargetPriceSlider = (props: Props) => {
  const cn = useStyles()
  // const { t } = useTranslation()

  if (!props.salesPrice || props.salesPrice.value === 0 || !props.targetPrice)
    return null

  const ratio = 1 - props.targetPrice.value / props.salesPrice.value

  const className =
    props.salesPrice.value === 0
      ? cn.warning
      : ratio > 0
      ? cn.error
      : ratio > -0.3
      ? cn.success
      : cn.info

  return (
    <TargetPriceSliderContext.Provider value={props}>
      <StyledSlider
        value={[props.salesPrice.value, props.targetPrice.value]}
        className={classNames(className, props.rootClassName)}
        min={
          Math.min(props.salesPrice.value, props.targetPrice.value) -
          0.1 * Math.min(props.salesPrice.value, props.targetPrice.value)
        }
        max={
          Math.max(props.salesPrice.value, props.targetPrice.value) +
          0.1 * Math.max(props.salesPrice.value, props.targetPrice.value)
        }
        track={'normal'}
        valueLabelDisplay="auto"
        ValueLabelComponent={(p) => <ValueLabelComponent {...p} {...props} />}
        ThumbComponent={ThumbComponent as never}
        onClick={() =>
          props.openTargetPricePopOver && props.openTargetPricePopOver()
        }
      />
      {/* {ratio !== 0 && (
        <Tooltip
          title={t(
            `project:targetPriceAboveBelow_${ratio < 0 ? 'above' : 'below'}`,
            {
              defaultValue: `target price is {{ratio}}% ${
                ratio < 0 ? 'above' : 'below'
              } the sales price`,
              replace: { ratio: Math.abs(ratio * 100).toFixed(2) },
            }
          )}
          arrow
          placement="bottom"
        >
          <Typography variant="caption" color="textSecondary">
            {Math.abs(ratio * 100).toFixed(2)}%
          </Typography>
        </Tooltip>
      )} */}
    </TargetPriceSliderContext.Provider>
  )
}
